/**
 * DoctypePicker — the canonical surface for doctype selection.
 *
 * Phase 6.2 (2026-04-26), restyled to the launch film (2026-10-14): domain
 * tabs (Pharma · Medical Device · Shared) → rows grouped under mono category
 * headers (CLINICAL · QUALITY · REGULATORY, see ``categoryForEntry``) →
 * bordered rows of mono code · full name · governing guideline. Replaces the conversational chip
 * picker for doctype selection across every entry path.
 *
 * Used by:
 *   - `/draft asking-doctype` step (mid-conversation pick)
 *   - ContextStrip "pick a doctype" chip-click
 *   - Any future doctype-pick command alias
 *
 * Mirrors the LibraryHub design pattern (Fluent Dialog modal, single
 * canonical surface) so writers learn one selection idiom for both
 * special-case concepts (library, doctype). Per the "writer-input
 * pattern" rule (memory feedback): everything else uses user_input;
 * library and doctype are the two named exceptions with their own
 * canonical surfaces.
 *
 * SRP: pure presentation + selection. The picker owns rendering +
 * tab state + filter logic. The CALLER decides what happens
 * on select (start a draft, switch active doctype, etc.). No
 * knowledge of run state, draftFlow, libraries, or LoomFreshSelection.
 *
 * # DECISION: Show ALL catalog entries (including "coming soon"
 * #   ones without ``backendDocType``); render unsupported rows
 * #   in a disabled style with a "coming soon" hint.
 * # WHY: matches the screenshot the writer signed off on. Honest
 * #      UX — writers see the full ecosystem and which entries are
 * #      wired today. Filtering them out (the chip-picker behaviour)
 * #      hides the catalog's breadth and surprises writers when a
 * #      doctype "appears" in a later release. Honest absence beats
 * #      silent omission.
 * # DATE: 2026-04-26
 *
 * # DECISION: Expandable entries (NDA/BLA → M1-M5) render only
 * #   the parent in v1. Children deferred to a follow-up.
 * # WHY: YAGNI — most M-series children don't have ``backendDocType``
 * #      wired today, so adding the expand affordance would surface
 * #      five disabled rows that aren't actionable. When children
 * #      get wired, add the expand toggle then. Parent rows stay
 * #      visible + selectable when the parent itself is wired.
 * # DATE: 2026-04-26
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Select,
  Text,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import React, { useMemo, useState } from "react";
import type { AppConfig, DocTypeListEntry } from "../../api/client";
import type { Workspace } from "../../types";
import {
  DOC_CATALOG,
  DOC_CATEGORY_ORDER,
  DOMAIN_LABELS,
  type DocCatalogEntry,
  type DocCategory,
  type DocCatalogPhase,
  type DocDomain,
  categoryForEntry,
  isPrivateTemplateCode,
  phasesForDomain,
  privateTemplateName,
} from "../doc-catalog";
import { type DoctypeChoice, SUBMISSION_REGIONS } from "../draftFlow";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DoctypePickerProps {
  /** Modal visibility. False = closed; true = open. */
  open: boolean;
  /** Currently-selected doctype code (highlighted in the list).
   *  Null when nothing is selected yet (idle / first pick). */
  selectedDocCode: string | null;
  /** Backend runtime config. Drives sandbox-availability filtering
   *  + the unsupported-entry "coming soon" hint. Null = treat all
   *  entries as supported (live mode, no filtering). */
  appConfig: AppConfig | null;
  /** Restrict the visible catalog to these doctype codes.
   *
   *  ``null`` / omitted = no restriction, the full catalog renders (the
   *  normal case). A non-null list narrows the picker to those codes:
   *  non-matching entries, now-empty phase groups, and now-empty domain
   *  tabs are all dropped.
   *
   *  Resolved in VariantCShell from the backend's tier-filtered
   *  ``GET /doc-types`` (:func:`useAllowedDocCodes`) — a ``pay_per_use`` account gets
   *  only the ``doctypes/doc_pricing.json`` codes, paid tiers get all. Passed
   *  ``[]`` while that load is in flight / on error, so the picker fails
   *  closed (shows nothing) rather than falling open to the full catalog.
   *  Entries are FILTERED OUT rather than disabled — the file's existing
   *  "show everything, disable what isn't wired" decision is about honest
   *  absence within the product's scope, but a reviewer looking at a page of
   *  greyed rows reads it as an unfinished add-in, which is the thing
   *  AppSource certification rejects. */
  allowedDocCodes?: readonly string[] | null;
  /** Writer picked a doctype. Caller decides what to do with the
   *  choice (start a draft, just update active, etc.). */
  onSelect: (choice: DoctypeChoice) => void;
  /** Writer cancelled (Cancel button or Escape). */
  onCancel: () => void;
  /** When true, the picker is being used to identify the type of an EXISTING
   *  document (build-on / custom submission) rather than choosing what to draft.
   *  Only changes the header copy. */
  customImport?: boolean;
  /** Workspaces the writer belongs to. When given (with ``onWorkspaceChange``)
   *  a workspace selector heads the dialog, so the writer can see each
   *  workspace's private templates. Selecting one IS the shell's workspace
   *  selection — the run is started in the workspace the template came from. */
  workspaces?: readonly Workspace[];
  selectedWorkspaceId?: string | null;
  onWorkspaceChange?: (workspaceId: string) => void;
  /** Private templates linked to ``selectedWorkspaceId`` (from
   *  ``GET /doc-types?workspace_id=``). ``null`` while loading. Rendered on a
   *  "Workspace templates" tab ahead of the public catalog. */
  privateTemplates?: readonly DocTypeListEntry[] | null;
  /** Submission regions to offer (``{code: label}`` from ``GET /doc-types``).
   *  Empty / omitted falls back to ``SUBMISSION_REGIONS``. */
  regions?: Readonly<Record<string, string>> | null;
  /** Open straight on the region step for this doctype — used when a draft
   *  starts from a saved doctype that has no region yet. */
  regionStepFor?: DoctypeChoice | null;
}

/** Tab keys: the domain tabs (Pharma · Medical Device · Shared), plus a
 *  "Workspace templates" tab ahead of them when the selected workspace has
 *  private templates. */
const WORKSPACE_TAB = "workspace" as const;
type PickerTab = DocDomain | typeof WORKSPACE_TAB;

// ---------------------------------------------------------------------------
// Pure helper — exported for tests
// ---------------------------------------------------------------------------

/** Compute whether a catalog entry is selectable in the given app
 *  config. Pure: same inputs → same output. */
export function isDoctypeAvailable(entry: DocCatalogEntry, appConfig: AppConfig | null): boolean {
  if (!entry.backendDocType) return false;
  if (!appConfig?.sandbox) return true;
  const sandboxList = appConfig.sandbox_available_doc_types ?? [];
  return sandboxList.includes(entry.backendDocType);
}

/** Phases for a domain, narrowed to ``allowedDocCodes``.
 *
 *  ``null``/undefined returns the domain's phases untouched. Otherwise
 *  each phase keeps only its matching docs, and phases left with no docs
 *  are dropped so no empty group header renders. Pure. */
export function visiblePhasesForDomain(
  domain: DocDomain,
  allowedDocCodes?: readonly string[] | null,
): DocCatalogPhase[] {
  const phases = phasesForDomain(domain);
  if (!allowedDocCodes) return phases;
  const allowed = new Set(allowedDocCodes);
  return phases
    .map((phase) => ({ ...phase, docs: phase.docs.filter((entry) => allowed.has(entry.code)) }))
    .filter((phase) => phase.docs.length > 0);
}

/** Domains that still have at least one visible doc under
 *  ``allowedDocCodes``. Used to drop domain tabs that would open onto an
 *  empty list. Pure. */
export function visibleDomains(allowedDocCodes?: readonly string[] | null): DocDomain[] {
  const domains = Object.keys(DOMAIN_LABELS) as DocDomain[];
  if (!allowedDocCodes) return domains;
  return domains.filter((domain) => visiblePhasesForDomain(domain, allowedDocCodes).length > 0);
}

/** One row of the flat, category-grouped list: the entry plus the domain
 *  it came from (``entryToChoice`` needs the domain). */
export interface CatalogRow {
  entry: DocCatalogEntry;
  domain: DocDomain;
}

/** The catalog grouped by ``DocCategory`` (launch-film screen 1), narrowed
 *  to ``allowedDocCodes`` and, when ``onlyDomain`` is given, to that one
 *  domain tab.
 *
 *  Walks pharma → device → shared in phase order, so rows keep their
 *  catalog order inside a category. A code listed in two domains (IB sits
 *  under pharma AND shared) renders once — the first, which is also what
 *  ``lookupDoctypeChoice`` resolves it to. Empty categories are dropped.
 *  Pure. */
export function catalogGroups(
  allowedDocCodes?: readonly string[] | null,
  onlyDomain?: DocDomain,
): Array<{ category: DocCategory; rows: CatalogRow[] }> {
  const byCategory = new Map<DocCategory, CatalogRow[]>();
  const seen = new Set<string>();
  const domains = onlyDomain ? [onlyDomain] : (Object.keys(DOMAIN_LABELS) as DocDomain[]);
  for (const domain of domains) {
    for (const phase of visiblePhasesForDomain(domain, allowedDocCodes)) {
      for (const entry of phase.docs) {
        if (seen.has(entry.code)) continue;
        seen.add(entry.code);
        const category = categoryForEntry(entry, domain, phase.phase);
        const rows = byCategory.get(category) ?? [];
        rows.push({ entry, domain });
        byCategory.set(category, rows);
      }
    }
  }
  return DOC_CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => ({
    category,
    rows: byCategory.get(category) ?? [],
  }));
}

/** Convert a catalog entry to the DoctypeChoice payload onSelect
 *  emits. Pure. Caller invokes only when ``isDoctypeAvailable``
 *  returned true; this helper trusts the caller to honour that. */
export function entryToChoice(entry: DocCatalogEntry, domain: DocDomain): DoctypeChoice {
  return {
    docCode: entry.code,
    backendDocType: entry.backendDocType ?? "",
    docFramework: entry.framework ?? null,
    docName: entry.name,
    domain,
    auth: entry.auth ?? null,
  };
}

/** The ``DoctypeChoice`` for a private workspace template. Its UI code is its
 *  backend code (``WS_*``); it lives outside the static catalog. Pure. */
export function privateTemplateChoice(code: string, name: string): DoctypeChoice {
  return {
    docCode: code,
    backendDocType: code,
    docFramework: null,
    docName: name,
    domain: "shared",
    auth: null,
  };
}

/** Find which domain (pharma/device/shared) currently contains the
 *  selected doctype code. Falls back to ``"pharma"`` when not found
 *  or when no selection exists. Pure. */
export function domainForDocCode(docCode: string | null): DocDomain {
  if (!docCode) return "pharma";
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return domain;
        if (entry.children?.some((c) => c.code === docCode)) return domain;
      }
    }
  }
  return "pharma";
}

/** Walk the catalog to recover the full ``DoctypeChoice`` payload
 *  for a stored doctype code. Pure: same inputs → same output, no
 *  React. Returns null when the code isn't in the catalog (e.g. the
 *  catalog was edited and the writer's stored selection became
 *  stale) — caller MUST treat that as "no doctype selected" rather
 *  than fabricating a partial choice.
 *
 *  Phase 7.2 — independent ``selectedDocCode`` state in the shell
 *  stores only the code string; the rest of the choice is derived
 *  here on demand. Single source of truth = the catalog. */
export function lookupDoctypeChoice(docCode: string | null): DoctypeChoice | null {
  if (!docCode) return null;
  // A private workspace template is not in the static catalog; its code is
  // its backend doc_type. Never null for one, so a stored selection survives
  // even before the workspace's template list has loaded.
  if (isPrivateTemplateCode(docCode)) return privateTemplateChoice(docCode, privateTemplateName(docCode) ?? docCode);
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return entryToChoice(entry, domain);
        if (entry.children) {
          for (const child of entry.children) {
            if (child.code === docCode) return entryToChoice(child, domain);
          }
        }
      }
    }
  }
  return null;
}

/** The ``[code, label]`` region options to render, in server order, falling
 *  back to ``SUBMISSION_REGIONS`` when the server sent none. Pure. */
export function regionOptions(regions?: Readonly<Record<string, string>> | null): Array<[string, string]> {
  const entries = Object.entries(regions ?? {});
  return entries.length > 0 ? entries : Object.entries(SUBMISSION_REGIONS);
}

/** Split a region label ("United Kingdom — MHRA") into the place and the
 *  agency that assesses it. A label without an agency ("Multiple regions")
 *  keeps an empty agency. Pure. */
export function splitRegionLabel(label: string): { place: string; agency: string } {
  const at = label.indexOf(" — ");
  if (at < 0) return { place: label.trim(), agency: "" };
  return { place: label.slice(0, at).trim(), agency: label.slice(at + 3).trim() };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// Shared mono-label treatment (category headers, codes, frameworks).
const monoLabel = {
  fontFamily: fonts.mono,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
} as const;

const useStyles = makeStyles({
  surface: {
    maxWidth: "560px",
    width: "92vw",
    background: "#fff",
  },
  title: {
    fontFamily: fonts.body,
    fontSize: "16px",
    fontWeight: 600,
    color: inkColors.ink,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "72vh",
    overflowY: "auto",
    paddingRight: "2px",
  },
  // Workspace-templates vs catalogue switch — only when the workspace has
  // private templates. Compact mono tabs, same idiom as the panel tabs.
  tabs: {
    display: "flex",
    gap: "16px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  tab: {
    ...monoLabel,
    padding: "6px 0 7px",
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    fontSize: "10.5px",
    fontWeight: 600,
    color: inkColors.ink4,
    "&:hover": { color: inkColors.ink2 },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
  },
  tabActive: {
    color: brandColors.primary,
    borderBottom: `2px solid ${brandColors.primary}`,
  },
  tabCount: {
    marginLeft: "6px",
    padding: "0 5px",
    borderRadius: "8px",
    background: brandColors.light,
    color: brandColors.primary,
  },
  groupList: {
    display: "flex",
    flexDirection: "column",
  },
  groupLabel: {
    ...monoLabel,
    display: "block",
    fontSize: "10px",
    fontWeight: 500,
    color: inkColors.ink4,
    padding: "10px 2px 6px",
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  docRow: {
    display: "grid",
    gridTemplateColumns: "84px minmax(0, 1fr) minmax(0, 128px)",
    alignItems: "center",
    columnGap: "10px",
    // Narrow task pane: two columns, the guideline drops under the title.
    "@media (max-width: 520px)": {
      gridTemplateColumns: "72px minmax(0, 1fr)",
      columnGap: "8px",
    },
    padding: "9px 12px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    fontFamily: fonts.body,
    "&:hover": {
      border: `1px solid ${brandColors.primary}`,
      background: brandColors.light,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  docRowDisabled: {
    cursor: "not-allowed",
    opacity: 0.5,
    "&:hover": {
      border: `1px solid ${surfaceColors.line}`,
      background: "#fff",
    },
  },
  docRowSelected: {
    border: `1px solid ${brandColors.primary}`,
    background: brandColors.light,
  },
  docCode: {
    ...monoLabel,
    letterSpacing: "0.3px",
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink,
    overflowWrap: "anywhere",
  },
  docCodeSelected: {
    color: brandColors.primary,
  },
  docMain: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  docName: {
    fontSize: "13px",
    color: inkColors.ink2,
    lineHeight: 1.3,
  },
  docMeta: {
    ...monoLabel,
    fontSize: "9.5px",
    color: inkColors.ink4,
    marginTop: "2px",
  },
  docFramework: {
    ...monoLabel,
    letterSpacing: "0.3px",
    fontSize: "10px",
    color: inkColors.ink4,
    textAlign: "right",
    overflowWrap: "anywhere",
    "@media (max-width: 520px)": {
      gridColumn: "2",
      textAlign: "left",
      marginTop: "2px",
    },
  },
  privateTag: {
    ...monoLabel,
    fontSize: "9px",
    color: brandColors.primary,
  },
  workspaceRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  workspaceLabel: {
    ...monoLabel,
    fontSize: "10px",
    fontWeight: 600,
    color: inkColors.ink4,
    whiteSpace: "nowrap",
  },
  workspaceSelect: {
    flex: 1,
    minWidth: 0,
  },
  emptyTemplates: {
    fontSize: "12px",
    color: inkColors.ink4,
    fontFamily: fonts.body,
    padding: "8px 2px",
  },
  // -- Region step ----------------------------------------------------------
  // Same radio list as the folder and library pickers (HomePromptDialog,
  // HubLibraryList): one bordered list, hairline rows, a radio dot, the place
  // on the first line and its agency under it. Two lines per row so the list
  // fits a ~320px task pane without truncating "EMA / notified bodies".
  regionIntro: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    margin: 0,
  },
  // The document being filed, with Change back to the catalog — the same
  // bordered row as the section picker's "Save as … · Change".
  regionDoc: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "9px 12px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderLeft: `3px solid ${brandColors.primary}`,
    background: surfaceColors.paper,
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.4,
    flexShrink: 0,
  },
  regionDocLabel: { color: inkColors.ink3, flexShrink: 0 },
  regionDocValue: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: inkColors.ink,
    fontWeight: 600,
  },
  regionDocAction: {
    flexShrink: 0,
    background: "transparent",
    border: "none",
    padding: 0,
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: "inherit",
    fontWeight: 600,
    color: brandColors.primary,
    cursor: "pointer",
    ":hover": { textDecoration: "underline" },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
  },
  regionList: {
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    flexShrink: 0,
  },
  regionOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: fonts.body,
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderTop: `1px solid ${surfaceColors.line}`,
    padding: "8px 12px",
    cursor: "pointer",
    ":first-child": { borderTopColor: "transparent" },
    ":hover": { backgroundColor: surfaceColors.paper },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
  },
  // Radio dot: hollow ring; the chosen region gets a filled centre.
  radio: {
    width: "14px",
    height: "14px",
    marginTop: "2px",
    borderRadius: "50%",
    border: `1.5px solid ${inkColors.ink5}`,
    boxSizing: "border-box",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOn: {
    borderTopColor: brandColors.primary,
    borderRightColor: brandColors.primary,
    borderBottomColor: brandColors.primary,
    borderLeftColor: brandColors.primary,
    "::after": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: brandColors.primary,
    },
  },
  regionText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 },
  regionPlace: {
    fontSize: "13px",
    lineHeight: 1.3,
    fontWeight: 500,
    color: inkColors.ink,
    overflowWrap: "anywhere",
  },
  regionPlaceOn: { fontWeight: 600 },
  regionAgency: {
    fontSize: "11px",
    lineHeight: 1.35,
    fontWeight: 400,
    color: inkColors.ink4,
    overflowWrap: "anywhere",
  },
  // Continue in the brand accent, like the library and folder pickers.
  primary: {
    backgroundColor: brandColors.primary,
    ":hover": { backgroundColor: brandColors.primary, opacity: 0.92 },
  },
});

/** Second line for the region with no single agency. */
const GLOBAL_REGION_DETAIL = "Filed with more than one regulator";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DoctypePicker: React.FC<DoctypePickerProps> = ({
  open,
  selectedDocCode,
  appConfig,
  allowedDocCodes = null,
  onSelect,
  onCancel,
  customImport = false,
  workspaces,
  selectedWorkspaceId = null,
  onWorkspaceChange,
  privateTemplates = null,
  regions = null,
  regionStepFor = null,
}) => {
  const styles = useStyles();
  // Two steps: pick the doctype, then where it is submitted. The doctype is
  // held here until a region is chosen — nothing is emitted before both land.
  const [pendingChoice, setPendingChoice] = useState<DoctypeChoice | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setPendingChoice(regionStepFor);
    setRegion(null);
  }, [open, regionStepFor]);
  const showWorkspaceSelect = !!onWorkspaceChange && !!workspaces && workspaces.length > 0;
  const templates = privateTemplates ?? [];
  const hasTemplates = templates.length > 0;

  // # DECISION: keep the Pharma / Medical Device / Shared domain tabs at the
  //   top, with the film's category-grouped rows inside each tab.
  // # WHY: a customer drafts either pharma or device documents, so the
  //   domain is the first choice they make (Guru, 2026-10-14).
  // # DATE: 2026-10-14
  // Domain tabs that still have something behind them under the allow-list.
  const domains = useMemo(() => visibleDomains(allowedDocCodes), [allowedDocCodes]);

  // A workspace with templates opens on them: they are what that workspace
  // was set up to draft. Otherwise open on the selection's own domain.
  // Re-derived on every open so a reopen re-orients.
  const initialTab = useMemo((): PickerTab => {
    void open;
    if (hasTemplates && (!selectedDocCode || isPrivateTemplateCode(selectedDocCode))) return WORKSPACE_TAB;
    const preferred = domainForDocCode(selectedDocCode);
    return domains.includes(preferred) ? preferred : (domains[0] ?? "pharma");
  }, [selectedDocCode, open, hasTemplates, domains]);
  const [tab, setTab] = useState<PickerTab>(initialTab);
  React.useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Derived rather than stored, so templates disappearing mid-session can't
  // strand the picker on an empty templates view.
  const onWorkspaceTab = tab === WORKSPACE_TAB && (hasTemplates || privateTemplates === null);
  const activeDomain: DocDomain = tab !== WORKSPACE_TAB && domains.includes(tab) ? tab : (domains[0] ?? "pharma");
  const groups = useMemo(() => catalogGroups(allowedDocCodes, activeDomain), [allowedDocCodes, activeDomain]);
  const tabKeys: PickerTab[] = hasTemplates ? [WORKSPACE_TAB, ...domains] : domains;

  const handleSelect = (row: CatalogRow): void => {
    if (!isDoctypeAvailable(row.entry, appConfig)) return;
    setPendingChoice(entryToChoice(row.entry, row.domain));
  };
  const options = regionOptions(regions);
  const backToCatalog = (): void => {
    setPendingChoice(null);
    setRegion(null);
  };
  // Radio-group keyboard: Tab enters on the chosen region (or the first),
  // arrows / Home / End move and choose, Enter or Space choose the focused row.
  const regionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const focusIndex = Math.max(
    0,
    options.findIndex(([code]) => code === region),
  );
  const onRegionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const last = options.length - 1;
    let next: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next === null) return;
    e.preventDefault();
    setRegion(options[next][0]);
    regionRefs.current[next]?.focus();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle className={styles.title}>
            {pendingChoice
              ? "Where will this be submitted?"
              : customImport
                ? "Which type of document is this?"
                : "Pick a document type"}
          </DialogTitle>
          <DialogContent>
            {pendingChoice ? (
              <div className={styles.body}>
                <div className={styles.regionDoc}>
                  <span className={styles.regionDocLabel}>Filing</span>
                  <span className={styles.regionDocValue} title={pendingChoice.docName}>
                    {pendingChoice.docName}
                  </span>
                  <button
                    type="button"
                    className={styles.regionDocAction}
                    aria-label="Change document type"
                    onClick={backToCatalog}
                  >
                    Change
                  </button>
                </div>
                <p className={styles.regionIntro}>The agents apply this region's requirements and read its guidance.</p>
                <div className={styles.regionList} role="radiogroup" aria-label="Submission region">
                  {options.map(([code, label], index) => {
                    const selected = region === code;
                    const { place, agency } = splitRegionLabel(label);
                    const detail = agency || (code === "GLOBAL" ? GLOBAL_REGION_DETAIL : "");
                    return (
                      <button
                        type="button"
                        // biome-ignore lint/a11y/useSemanticElements: a styled radio row (a native radio cannot take this layout)
                        role="radio"
                        aria-checked={selected}
                        key={code}
                        ref={(el) => {
                          regionRefs.current[index] = el;
                        }}
                        tabIndex={index === focusIndex ? 0 : -1}
                        title={code}
                        data-region={code}
                        onClick={() => setRegion(code)}
                        onDoubleClick={() => onSelect({ ...pendingChoice, region: code })}
                        onKeyDown={(e) => onRegionKeyDown(e, index)}
                        className={styles.regionOption}
                      >
                        <span className={mergeClasses(styles.radio, selected && styles.radioOn)} aria-hidden="true" />
                        <span className={styles.regionText}>
                          <span className={mergeClasses(styles.regionPlace, selected && styles.regionPlaceOn)}>
                            {place}
                          </span>
                          {detail && <span className={styles.regionAgency}>{detail}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={styles.body}>
                {showWorkspaceSelect && (
                  <div className={styles.workspaceRow}>
                    <label htmlFor="doctype-picker-workspace" className={styles.workspaceLabel}>
                      Workspace
                    </label>
                    <Select
                      id="doctype-picker-workspace"
                      className={styles.workspaceSelect}
                      size="small"
                      value={selectedWorkspaceId ?? ""}
                      onChange={(_, data) => {
                        if (data.value) onWorkspaceChange?.(data.value);
                      }}
                    >
                      {!selectedWorkspaceId && <option value="">Choose a workspace…</option>}
                      {workspaces?.map((ws) => (
                        <option key={ws.id} value={ws.id}>
                          {ws.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {showWorkspaceSelect && privateTemplates !== null && !hasTemplates && (
                  <Text className={styles.emptyTemplates}>No private templates in this workspace.</Text>
                )}

                {/* Domain tabs, plus workspace templates when there are any. */}
                {tabKeys.length > 0 && (
                  <div className={styles.tabs} role="tablist" aria-label="Document domain">
                    {tabKeys.map((key) => {
                      const active = key === WORKSPACE_TAB ? onWorkspaceTab : !onWorkspaceTab && key === activeDomain;
                      return (
                        <button
                          type="button"
                          key={key}
                          role="tab"
                          aria-selected={active}
                          onClick={() => setTab(key)}
                          className={mergeClasses(styles.tab, active && styles.tabActive)}
                        >
                          {key === WORKSPACE_TAB ? "Workspace templates" : DOMAIN_LABELS[key].label}
                          {key === WORKSPACE_TAB && <span className={styles.tabCount}>{templates.length}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {onWorkspaceTab && (
                  <div className={styles.rows}>
                    {privateTemplates === null && <Text className={styles.emptyTemplates}>Loading templates…</Text>}
                    {templates.map((tpl) => {
                      const selected = selectedDocCode !== null && tpl.code === selectedDocCode;
                      const name = tpl.display_name || privateTemplateName(tpl.code) || tpl.code;
                      return (
                        <button
                          type="button"
                          key={tpl.code}
                          onClick={() => setPendingChoice(privateTemplateChoice(tpl.code, name))}
                          aria-current={selected ? "true" : undefined}
                          className={mergeClasses(styles.docRow, selected && styles.docRowSelected)}
                        >
                          <span className={styles.privateTag}>Private</span>
                          <span className={styles.docMain}>
                            <span className={styles.docName}>{name}</span>
                            <span className={styles.docMeta}>
                              {`v${tpl.version}`}
                              {typeof tpl.draftable_count === "number" && ` · ${tpl.draftable_count} sections to draft`}
                            </span>
                          </span>
                          <span className={styles.docFramework}>{tpl.base_doc_type || ""}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Category-grouped doctype rows */}
                {!onWorkspaceTab && (
                  <div className={styles.groupList}>
                    {groups.map(({ category, rows }) => {
                      const headingId = `doctype-cat-${category}`;
                      return (
                        <section key={category} aria-labelledby={groups.length > 1 ? headingId : undefined}>
                          {groups.length > 1 && (
                            <span id={headingId} className={styles.groupLabel}>
                              {category}
                            </span>
                          )}
                          <div className={styles.rows}>
                            {rows.map((row) => {
                              const { entry } = row;
                              const available = isDoctypeAvailable(entry, appConfig);
                              const selected = selectedDocCode !== null && entry.code === selectedDocCode;
                              return (
                                <button
                                  type="button"
                                  key={entry.code}
                                  onClick={() => handleSelect(row)}
                                  disabled={!available}
                                  aria-current={selected ? "true" : undefined}
                                  // The regulator tag is secondary — kept as a tooltip.
                                  title={
                                    entry.auth
                                      ? `${entry.auth}${entry.framework ? ` · ${entry.framework}` : ""}`
                                      : undefined
                                  }
                                  className={mergeClasses(
                                    styles.docRow,
                                    !available && styles.docRowDisabled,
                                    selected && styles.docRowSelected,
                                  )}
                                >
                                  <span className={mergeClasses(styles.docCode, selected && styles.docCodeSelected)}>
                                    {entry.code}
                                  </span>
                                  <span className={styles.docMain}>
                                    <span className={styles.docName}>{entry.name}</span>
                                    {!available && <span className={styles.docMeta}>coming soon</span>}
                                  </span>
                                  <span className={styles.docFramework}>{entry.framework ?? entry.auth ?? ""}</span>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
            {pendingChoice && (
              <Button
                appearance="primary"
                // Accent only when enabled, so the disabled state keeps Fluent's grey.
                className={region ? styles.primary : undefined}
                disabled={!region}
                onClick={() => {
                  if (region) onSelect({ ...pendingChoice, region });
                }}
              >
                Continue
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DoctypePicker;
